import React, { useEffect, useState } from 'react';
import { Clapperboard, Film, Images, Layers, RectangleHorizontal, RectangleVertical, Square } from 'lucide-react';
import type { StartWorkflow } from '../types';
import { IMAGEMOTION_LOGO_SRC, VIDEOMOTION_LOGO_SRC } from '../constants';
import type { SavedBrandKit } from '../services/supabase';
import { formatLabel } from './editor/model';

export type NewProjectFormat = 'landscape' | 'vertical' | 'square';

interface FormatPickerProps {
  busy: boolean;
  /** Null while saved branding is still loading. */
  brandKits: SavedBrandKit[] | null;
  /**
   * `undefined` means the default branding (or none, if none is saved).
   * `null` starts the video without saved branding.
   */
  onSelect: (format: NewProjectFormat, workflow: StartWorkflow, brandKitId?: string | null) => void;
  onCancel: () => void;
}

const CARDS: Array<{
  format: NewProjectFormat;
  title: string;
  detail: string;
  size: string;
}> = [
  {
    format: 'landscape',
    title: 'Landscape',
    detail: '16:9',
    size: '1920 × 1080',
  },
  {
    format: 'vertical',
    title: 'Portrait',
    detail: '9:16',
    size: '1080 × 1920',
  },
  {
    format: 'square',
    title: 'Square',
    detail: '1:1',
    size: '1080 × 1080',
  },
];

const WORKFLOWS: Array<{
  id: StartWorkflow;
  title: string;
  detail: string;
  icon: React.ComponentType<{ className?: string }>;
  logo?: string;
}> = [
  {
    id: 'standard',
    title: 'Standard video',
    detail: 'Upload your photos and video clips, put them in order, trim them and add music.',
    icon: Film,
  },
  {
    id: 'slideshow',
    title: 'Photo slideshow',
    detail: 'Upload listing photos. Each one holds still for 5 seconds, and you can animate any of them with ImageMotion.',
    icon: Images,
    logo: IMAGEMOTION_LOGO_SRC,
  },
  {
    id: 'walkthrough',
    title: 'VideoMotion walkthrough',
    detail: 'Upload walkthrough footage, put the rooms in order, trim it and apply VideoMotion speed ramps.',
    icon: Clapperboard,
    logo: VIDEOMOTION_LOGO_SRC,
  },
  {
    id: 'mixed',
    title: 'Photos and footage with AI',
    detail: 'Combine photos and footage, animate photos with ImageMotion and speed-ramp footage with VideoMotion.',
    icon: Layers,
  },
];

export default function FormatPicker({ busy, brandKits, onSelect, onCancel }: FormatPickerProps) {
  const [format, setFormat] = useState<NewProjectFormat | null>(null);
  const [brandKitId, setBrandKitId] = useState<string | null | undefined>(undefined);
  const kits = brandKits ?? [];
  const resolvedId = brandKitId === undefined
    ? (brandKits
      ? brandKits.find((kit) => kit.isDefault)?.id ?? brandKits[0]?.id ?? null
      : undefined)
    : brandKitId;
  const selected = kits.find((kit) => kit.id === resolvedId) ?? null;

  useEffect(() => {
    if (!brandKits) return;
    setBrandKitId((current) => {
      if (current === null) return null;
      if (typeof current === 'string' && brandKits.some((kit) => kit.id === current)) return current;
      return brandKits.find((kit) => kit.isDefault)?.id ?? brandKits[0]?.id ?? null;
    });
  }, [brandKits]);

  const brandingNote = !brandKits || kits.length === 0
    ? null
    : selected
      ? `${selected.name} will be added to this video.`
      : 'This video will start without saved branding.';

  return (
    <div className="max-w-3xl mx-auto py-8 animate-fade-in">
      <button
        type="button"
        onClick={format ? () => setFormat(null) : onCancel}
        className="text-sm text-slate-400 hover:text-white mb-6"
      >
        ← {format ? 'Format' : 'Projects'}
      </button>
      <p className="text-xs uppercase tracking-[0.2em] text-teal-300/80">PropertyCuts</p>
      <h1 className="text-2xl font-semibold tracking-tight mt-1">Create property videos from photos and footage.</h1>

      {!format ? (
        <>
          <p className="text-sm text-slate-400 mt-2">Choose a format. You can mix photos, footage and animated clips in any project.</p>
          {kits.length > 1 && (
            <label className="block mt-4 max-w-sm">
              <span className="text-sm text-slate-300">Branding</span>
              <select
                value={resolvedId ?? ''}
                onChange={(event) => setBrandKitId(event.target.value || null)}
                className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm text-white"
              >
                <option value="">No saved branding</option>
                {kits.map((kit) => (
                  <option key={kit.id} value={kit.id}>
                    {kit.name}{kit.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          {brandingNote && (
            <p className="text-sm text-teal-200/90 mt-3">{brandingNote}</p>
          )}
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {CARDS.map((card) => (
              <button
                key={card.format}
                type="button"
                disabled={busy}
                onClick={() => setFormat(card.format)}
                className="text-left rounded-2xl border border-slate-800 bg-slate-900/50 hover:border-teal-500/50 hover:bg-slate-900 px-5 py-6 transition-colors disabled:opacity-60 flex flex-col"
              >
                <div className="mb-5 h-48 grid place-items-center">
                  <div
                    className={`rounded-lg bg-slate-800/80 border border-slate-700/80 grid place-items-center ${
                      card.format === 'landscape'
                        ? 'aspect-video w-full'
                        : card.format === 'square'
                          ? 'aspect-square h-36'
                          : 'aspect-[9/16] h-48'
                    }`}
                  >
                    {card.format === 'landscape' ? (
                      <RectangleHorizontal className="w-8 h-8 text-teal-300" />
                    ) : card.format === 'square' ? (
                      <Square className="w-8 h-8 text-teal-300" />
                    ) : (
                      <RectangleVertical className="w-8 h-8 text-teal-300" />
                    )}
                  </div>
                </div>
                <p className="text-lg font-medium">
                  {card.title} <span className="text-slate-400 font-normal">— {card.detail}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">{card.size} MP4</p>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-400 mt-2">
            {formatLabel(format)}. How would you like to start?
          </p>
          {brandingNote && (
            <p className="text-sm text-teal-200/90 mt-3">{brandingNote}</p>
          )}
          <div className="grid gap-3 mt-8">
            {WORKFLOWS.map((workflow) => {
              const Icon = workflow.icon;
              return (
                <button
                  key={workflow.id}
                  type="button"
                  disabled={busy}
                  onClick={() => onSelect(format, workflow.id, resolvedId)}
                  className="text-left rounded-2xl border border-slate-800 bg-slate-900/50 hover:border-teal-500/50 hover:bg-slate-900 px-5 py-5 transition-colors disabled:opacity-60 flex items-start gap-4"
                >
                  <span className="w-11 h-11 shrink-0 rounded-xl bg-teal-500/10 text-teal-200 grid place-items-center">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-base font-medium">{workflow.title}</span>
                    <span className="block text-sm text-slate-400 mt-1">{workflow.detail}</span>
                  </span>
                  {workflow.logo && (
                    <img
                      src={workflow.logo}
                      alt=""
                      className="hidden sm:block h-8 w-44 shrink-0 object-contain object-right self-center"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
